import {
  Box,
  Grid,
  SelectChangeEvent,
  styled,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { ETCAppConfig } from "../../../shared/app-configs/etc-app-configs";
import { TCAppConfig } from "../../../shared/app-configs/tc-app-configs";
import { Utills } from "../../../shared/app-configs/utilit";
import { StyledButton } from "../../../shared/components/buttons/custom-button";
import FormBuilder from "../../../shared/components/form-builder/form-builder";
import { OptionType } from "../../../shared/components/form-builder/form-config.model";
import CustomTable from "../../../shared/components/table/custom-table";
import { useFormActions } from "../../../shared/hooks/form-builder.hooks";
import { useApi } from "../../../shared/hooks/useApi";
import useNotification from "../../../shared/hooks/useNotification";
import useRecordsStore, { Pagination } from "../../../shared/hooks/useRecordStore";
import { StringValidation } from "../../../shared/utils/custom-validation.utils";
import { useTransactionCertificateStore } from "../../../store/transaction-certificates.store";
import { getColumnsBySource } from "./cbLandingPageTable.config";

export const PageWrapper = styled("div")({
  padding: "0 25px 0px 25px",
  display: "grid",
  overflowY: "auto",
  flexDirection: "column",
  gap: "12px",
  flex: 1,
});

// Static TC Standards list
const TC_STANDARDS = [
  { value: 'CCS', label: 'CCS' },
  { value: 'GRS', label: 'GRS' },
  { value: 'OCS', label: 'OCS' },
  { value: 'RCS', label: 'RCS' },
  { value: 'RWS', label: 'RWS' },
  { value: 'RMS', label: 'RMS' },
  { value: 'RAS', label: 'RAS' },
  { value: 'RDS', label: 'RDS' },
  { value: 'MMS', label: 'MMS' }
];

const CBLandingPage = () => {
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [localSource, setLocalSource] = useState<string>("dTrackit");
  const { resetAllDatas } = useFormActions("")
  const { setDelayReason, setTCIssueDate, setEditTCInfo, cbLicensingCode, setTCStandardGOTS } = useTransactionCertificateStore();
  const [getSubmissionStatus, setSubmissionStatus] = useState<{ value: string; label: string }[]>([]);
  const { notify } = useNotification();
  const { setNewTCRequested } = useTransactionCertificateStore();
  const isCbUser = cbLicensingCode.startsWith("CB");
  const sourceOptions: OptionType[] = [
    { label: "TC (dTrackit)", value: "dTrackit" },
    // { label: "TC (historical)", value: "historical" }  
  ];
  const getTCSubmissionStatus = async () => {
    try {
      // Hardcoded submission status values instead of API call
      const submissionStatuses = ["Submitted", "Success", "Amending", "Rejected", "Created", "Draft"];
      const result = submissionStatuses.map((status: string) => ({ value: status, label: status }));

      setSubmissionStatus(result);
    } catch (err) {
      Utills.cl(`Error in setting TC Submission Status ${err}`);
    }
  };



  const links: OptionType[] = [
    { label: "TP1", value: "item1" },
    { label: "TP2", value: "item2" },
  ];
  const { executeGet } = useApi();

  const [singleValue, setSingleValue] = React.useState<string | number>("");

  const {
    dTrackitData,
    historicalData,
    eTrackitData,
    fetchRecords,
    selectedSource,
    setSelectedSource,
    isLoading,
    hasMorePages,
  } = useRecordsStore();
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [filteredData, setFilteredData] = useState(selectedSource === 'dTrackit' ? dTrackitData : selectedSource === 'historical' ? historicalData : eTrackitData);
  const columns = useMemo(() => getColumnsBySource(selectedSource),
    [selectedSource]);
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);

  const [isResetEnabled, setResetEnable] = useState(false);

  // Store last search parameters to maintain filters across pagination
  const [lastSearchParams, setLastSearchParams] = useState<any>(null);

  // Compute rowCount based on hasMorePages for pagination
  const computedRowCount = hasMorePages
    ? (pagination.pageIndex + 2) * pagination.pageSize // Fake count to enable Next button
    : (pagination.pageIndex + 1) * pagination.pageSize; // Exact count on last page

  useEffect(() => {
    setFilteredData(selectedSource === 'dTrackit' ? dTrackitData : selectedSource === 'historical' ? historicalData : eTrackitData);
  }, [selectedSource, dTrackitData, historicalData, eTrackitData]);
  const getCountry = async () => {
    try {
      // Import static country codes instead of making API call
      const countryCodesData = require("../../../shared/data/country-codes.json");

      // Most frequently used Textile Exchange seller/buyer countries (based on data analysis)
      const priorityCountries = ['CN', 'US', 'IN', 'BD', 'TR', 'PK', 'VN', 'GB', 'DE', 'IT', 'ES', 'FR', 'JP', 'KR', 'TW', 'HK', 'TH', 'ID', 'MY', 'SG'];

      // Transform to show "Country Name (CODE)" in dropdown
      const allCountries = countryCodesData
        .map((country: any) => ({
          value: country.code,
          label: `${country.name} (${country.code})`,
          isPriority: priorityCountries.includes(country.code)
        }));

      // Sort: priority countries first (alphabetically), then rest (alphabetically)
      const transformedData = allCountries.sort((a: any, b: any) => {
        if (a.isPriority && !b.isPriority) return -1;
        if (!a.isPriority && b.isPriority) return 1;
        return a.label.localeCompare(b.label);
      });

      Utills.cl(`getCountry static data loaded: ${transformedData.length} countries`);
      setCountries(transformedData);
    } catch (err) {
      Utills.cl(`Error loading static country codes: ${err}`);
    }
  };


  const handleTCNOChange = (value: any) => {
    formRef.current.setFieldValue("tcNo", value == null ? "" : value.value);
  };
  const getTCNoforSearch = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        tcNo: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
        tcCbLicensingCode: cbLicensingCode
      };
      const response: any = await executeGet(TCAppConfig.GET_TC_NO, queryParams);
      const data = response.data?.response?.length === 0 ? [] : response.data.response.map((item: any) => ({
        label: item,
        value: item,
      }));
      // //alert(JSON.stringify(response.data))
      return data;
    }
    catch (e) {
      console.error(`Error in Fetching TCNo ${e}`);
    }
  }
  const onNewRequestTCClick = () => {
    setNewTCRequested(true)
    navigate("/tc/create-tc/transaction-certificate")
  }
  useEffect(() => {
    const loadData = async () => {
      // If we have previous search params, use them; otherwise load default data
      if (lastSearchParams) {
        await fetchRecords({
          ...lastSearchParams,
          pagination,
        });
      } else {
        await fetchRecords({
          tcCbLicensingCode: cbLicensingCode,
          source: selectedSource === 'suppliers' ? 'dTrackit' : selectedSource ? selectedSource : 'dTrackit',
          pagination,
        });
      }
    };
    loadData();
  }, [pagination.pageIndex, pagination.pageSize, selectedSource]);

  useEffect(() => {
    setEditTCInfo({});
    setDelayReason(false)
    setTCIssueDate("")
    resetAllDatas();
    getTCSubmissionStatus()
    setTCStandardGOTS(false)
    // //alert(cbLicensingCode)
  }, []);

  useEffect(() => {
    getCountry();
  }, []); // Load once on mount - static data doesn't change


  const handlePageChange = useCallback(
    (newPagination: Pagination) => {
      setPagination({
        ...pagination,
        ...newPagination,
      });
    },
    [pagination, setPagination]
  );

  const formConfig = {
    pageName: "cb-landing-page",
    initialValues: {
      buyersCountry: [], // For multi-select
      startDate: null,
      endDate: null,
      tcStandard: [],
      source: "",
      submissionStatus: ""
    },
    validationSchema: Yup.object().shape({
      buyersCountry: Yup.array().nullable(),
      startDate: Yup.string().nullable(),
      endDate: Yup.string().nullable(),
      tcStandard: Yup.array().nullable(),
      source: Yup.string().nullable(),
      submissionStatus: Yup.string().nullable(),
    }),
    sections: [
      {
        fields: [
          {
            label: "Buyer's Country/Area",
            name: "buyersCountry",
            type: "select",
            gridSize: { xs: 12, sm: 6, md: 3, lg: 3 },
            required: false,
            props: {
              size: "small",
              multiple: true,
              enableSearch: true,
              options: countries,
              placeholder: "Select Buyer's Country",
            },
          },
          // Start Date Field
          {
            label: "Issued Start Date",
            name: "startDate",
            type: "date",
            gridSize: { xs: 12, sm: 6, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: "Select Start Date",
            },
          },
          // End Date Field
          {
            label: "Issued End Date",
            name: "endDate",
            type: "date",
            gridSize: { xs: 12, sm: 6, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: "Select End Date",
            },
          },
          {
            label: "TC No.",
            name: "tcNo",
            type: "search",
            gridSize: { xs: 12, sm: 6, md: 3, lg: 3 },
            required: false,
            props: {
              placeholder: "Search TC No.",
              onChange: handleTCNOChange,
              displayKey: "label",
              onSearch: (query: string, offset: number) => {
                const response = getTCNoforSearch(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Submission Status",
            name: "submissionStatus",
            type: "select",
            gridSize: { xs: 12, sm: 6, md: 3, lg: 3 },
            required: false,
            props: {
              size: "small",
              multiple: false,
              options: getSubmissionStatus,
              placeholder: "Select Submission Status",
            },
          },
          {
            label: "TC Standard",
            name: "tcStandard",
            type: "select",
            gridSize: { xs: 12, sm: 6, md: 3, lg: 3 },
            required: false,
            props: {
              size: "small",
              multiple: true,
              options: TC_STANDARDS,
              placeholder: "Select TC Standard",
            },
          },
          {
            label: "Source",
            name: "source",
            type: "select",
            gridSize: { xs: 12, sm: 6, md: 3, lg: 3 },
            required: false,
            props: {
              size: "small",
              multiple: false,
              options: sourceOptions,
              value: localSource,
              onChange: (event: any) => {
                setLocalSource(event.target.value);
                setSelectedSource(event.target.value);
                formRef.current?.setFieldValue('source', event.target.value, false);
                checkFormValues();
              }
            },
          }

        ],
      },
    ],
    handleSubmit: async (values: any) => {
      await getCBLandingPageRecords(values, 1);
    },
  };

  const getCBLandingPageRecords = async (values: any, isReseted?: number) => {
    try {
      if (isReseted == 1) {
        const isEmpty = (val: any) => StringValidation.isNullOrEmpty(val);
        const allFieldsEmpty = [values.tcNo, values.submissionStatus, values.startDate, values.endDate].every(isEmpty) && (!values.buyersCountry?.length) && (!values.tcStandard?.length);

        if (allFieldsEmpty) {
          notify("Please fill at least one field", "error");
          return;
        }
      }

      // Store search params for pagination
      const searchParams = {
        tcCbLicensingCode: cbLicensingCode,
        startDate: values.startDate,
        endDate: values.endDate,
        tcNo: values.tcNo,
        submissionStatus: values.submissionStatus,
        buyersCountry: values.buyersCountry?.length > 0 ? JSON.stringify(values.buyersCountry) : undefined,
        tcStandard: values.tcStandard.length > 0 ? JSON.stringify(values.tcStandard) : undefined,
        source: selectedSource === 'suppliers' ? 'dTrackit' : selectedSource,
      };

      setLastSearchParams(searchParams);

      // Reset pagination to page 0 when applying new filters
      const resetPagination = { pageIndex: 0, pageSize: pagination.pageSize };
      setPagination(resetPagination);

      await fetchRecords({
        ...searchParams,
        pagination: resetPagination,
      });
      if (isReseted == 1) {
        setResetEnable(true)
      }
      else {
        setResetEnable(false)
      }
    } catch (error) {
      console.log("Error fetching data: ", error);
    }
  };

  const checkFormValues = () => {
    const currentValues = formRef.current?.values;
    if (currentValues) {
      const isAnyValueFilled = Object.values(currentValues).some(
        value => (Array.isArray(value) && value.length > 0) ||
          (value !== null && value !== "" && value !== undefined)
      );
      setIsSearchEnabled(isAnyValueFilled);
    }
  };

  const handleRowClick = (row: any) => {
    setNewTCRequested(false)
    // Utills.cl(row);
    // if (row.tcMetadataStatus === "Submitted") {
    //   navigate(`/tc/submited-tc/transaction-certificate/${row.tcId}`);
    // } else {
    //   navigate(`/tc/tc-details/transaction-certificate/${row.tcId}`);
    // }
  };

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const handleReset = async () => {
    formRef.current.resetForm();
    setLastSearchParams(null); // Clear stored search params
    const params = {
      tcCbLicensingCode: cbLicensingCode,
      startDate: null,
      endDate: null,
      tcNo: null,
      buyersCountry: [],
      tcStandard: [],
      source: selectedSource,
      submissionStatus: null,
      pagination: pagination
    }

    setResetEnable(false)
    await getCBLandingPageRecords(params, 0);
  }

  const handleSingleChange = (
    event: SelectChangeEvent<string | number | string[]>
  ) => {
    setSingleValue(event.target.value as string | number);
  };
  Utills.cl(columns, filteredData);

  return (
    <>
      <PageWrapper>
        <Typography variant="h6" gutterBottom align="left" fontWeight={"600"}>
          Certified Transactions
        </Typography>
        {isCbUser && <Box
          display="flex"
          justifyContent="flex-end" // Align items to the end of the container
          alignItems="center" // Vertically center the button and select
          width="100%" // Make the box span the entire width
          mb="12px"
          gap={2} // Add gap for spacing between elements
        >
          {
            localSource === "eTrackit" ? null : <StyledButton
              onClick={onNewRequestTCClick}
              variant="contained"
              color="warning"
              sx={{ height: '40px', mb: 2, boxShadow: "none" }} // Ensure consistent button height
            >
              Create TC
            </StyledButton>
          }

          {/*<Grid md={2} lg={2}>
            <CustomSelect
              size="small"
              value={singleValue}
              options={links}
              placeholder="Login To"
              onChange={handleSingleChange}
              sx={{ minWidth: '180px', height: '40px' }}
            />
          </Grid>*/}
        </Box>}
        <FormBuilder
          formConfig={formConfig}
          onSubmit={formConfig.handleSubmit}
          formRef={formRef}
        />
        {/* </Grid> */}

        <Grid
          item
          md={2}
          lg={2}
          display="flex"
          justifyContent={"flex-end"}
          alignItems="center"
        >
          <StyledButton onClick={handleSubmit}>Search</StyledButton>
          {isResetEnabled ?
            <StyledButton onClick={handleReset}>Reset</StyledButton>
            : null
          }
        </Grid>

        {/* </Grid> */}

        <CustomTable
          key={selectedSource}
          columns={columns}
          columnVisibility={selectedSource === "dTrackit" ? { tcId: false } : {}}
          data={filteredData}
          title="TransactionData"
          enableTopToolbar={true}
          enableBottomToolbar={true}
          // enableColumnOrdering={false}
          paginations={pagination} // Pass current pagination state
          setPaginations={setPagination}
          rowCount={computedRowCount}
          isLoadingTodos={isLoading}
          onClickRow={handleRowClick}
          hiddenColumns={["tcParentMetadataStatus", "tcId"]}
        />
      </PageWrapper>

    </>
  );
};

export default CBLandingPage;